import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Link,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import StopIcon from "@mui/icons-material/Stop";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PageBody from "../GeneralComponents/PageBody";
import PageBgBlock from "../GeneralComponents/PageBgBlock";
import ModalStandardCancel from "../GeneralComponents/ModalStandardCancel";
import GeneralContext from "../GeneralContext/GeneralContext";
import axios from "../../api/axios";
import dayjs from "dayjs";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const PROCESOS = [
  { tipo: "sagec", etiqueta: "Prefactura SAGEC" },
  { tipo: "operador", etiqueta: "Prefactura operador" },
];

const ESTADOS = {
  pendiente: { etiqueta: "Pendiente", color: "rgba(0,0,0,0.55)", fondo: "rgba(0,0,0,0.06)" },
  en_curso: { etiqueta: "En curso", color: "#1565c0", fondo: "rgba(21,101,192,0.12)" },
  ok: { etiqueta: "Generada", color: "#2E7D32", fondo: "rgba(46,125,50,0.12)" },
  error: { etiqueta: "Error", color: "#C62828", fondo: "rgba(198,40,40,0.12)" },
  omitido: { etiqueta: "Omitido", color: "#B35300", fondo: "rgba(237,108,2,0.14)" },
  cancelado: { etiqueta: "Cancelado", color: "rgba(0,0,0,0.55)", fondo: "rgba(0,0,0,0.06)" },
};

const TERMINADOS = ["ok", "error", "omitido", "cancelado"];

function periodosDisponibles() {
  const base = dayjs().startOf("month");
  return Array.from({ length: 36 }, (_, i) =>
    base.subtract(i, "month").format("YYYY-MM-01")
  );
}

function etiquetaPeriodo(periodo) {
  if (!periodo) return "—";
  const d = dayjs(periodo);
  return `${MESES[d.month()]} ${d.year()}`;
}

function periodosEnRango(desde, hasta) {
  if (!desde || !hasta) return [];
  let cursor = dayjs(desde).startOf("month");
  const fin = dayjs(hasta).startOf("month");
  if (cursor.isAfter(fin)) return [];

  const salida = [];
  while (!cursor.isAfter(fin)) {
    salida.push(cursor.format("YYYY-MM-01"));
    cursor = cursor.add(1, "month");
  }
  return salida;
}

function numero(valor) {
  if (valor == null) return "—";
  return Number(valor).toLocaleString("es-CL");
}

async function describirError(e) {
  const status = e?.response?.status;
  const data = e?.response?.data;

  let detalle = null;
  try {
    if (data instanceof Blob) {
      detalle = JSON.parse(await data.text())?.detail;
    } else if (data) {
      detalle = data.detail;
    }
  } catch {
    detalle = null;
  }

  if (detalle) return detalle;
  if (status === 403) return "No tienes privilegios para generar prefacturas.";
  if (status === 404) return "No se encontraron datos para este período.";
  if (e?.code === "ECONNABORTED") return "La generación superó el tiempo de espera del navegador; puede haber quedado corriendo en el servidor.";
  return "No se pudo generar la prefactura. Intenta nuevamente.";
}

function construirPlan(periodos, objetivos) {
  const pasos = [];
  periodos.forEach((periodo) => {
    PROCESOS.forEach((proceso) => {
      objetivos.forEach((objetivo) => {
        pasos.push({
          periodo,
          tipo: proceso.tipo,
          etiquetaProceso: proceso.etiqueta,
          idOperador: objetivo.id_operador ?? null,
          nombreOperador: objetivo.nombre_operador,
          estado: "pendiente",
          resultado: null,
          mensaje: null,
        });
      });
    });
  });
  return pasos;
}

function EstadoChip({ estado }) {
  const definicion = ESTADOS[estado] ?? ESTADOS.pendiente;
  return (
    <Chip
      size="small"
      label={definicion.etiqueta}
      sx={{
        color: definicion.color,
        background: definicion.fondo,
        fontWeight: 600,
        borderRadius: 0.5,
      }}
    />
  );
}

function DetallePaso({ paso }) {
  const exitosos = paso.resultado?.exitosos ?? [];
  const noEncontrados = paso.resultado?.no_encontrados ?? [];

  return (
    <Box sx={{ px: 2, py: 1.5, background: "rgba(0,0,0,0.02)" }}>
      {paso.mensaje && (
        <Typography variant="body2" sx={{ mb: 1, color: "#C62828" }}>
          {paso.mensaje}
        </Typography>
      )}

      {exitosos.length > 0 && (
        <Box sx={{ mb: noEncontrados.length ? 1.5 : 0 }}>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Archivos generados
          </Typography>
          {exitosos.map((fila, i) => (
            <Typography key={`ok-${i}`} variant="body2" sx={{ mt: 0.5 }}>
              Operador {fila.id_operador} · v{fila.version} · {numero(fila.rbds_totales)} RBD
              {fila.rbd_faltantes?.length ? ` · faltan ${fila.rbd_faltantes.length}` : ""}
              {fila.rbd_adicionales?.length ? ` · adicionales ${fila.rbd_adicionales.length}` : ""}
              {fila.url && (
                <>
                  {" · "}
                  <Link href={fila.url} target="_blank" rel="noopener noreferrer">
                    abrir archivo
                  </Link>
                </>
              )}
            </Typography>
          ))}
        </Box>
      )}

      {noEncontrados.length > 0 && (
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>
            Sin datos
          </Typography>
          {noEncontrados.map((fila, i) => (
            <Typography key={`no-${i}`} variant="body2" sx={{ mt: 0.5 }}>
              Operador {fila.id_operador}: {fila.detalle}
            </Typography>
          ))}
        </Box>
      )}

      {!paso.mensaje && !exitosos.length && !noEncontrados.length && (
        <Typography variant="body2" sx={{ color: "rgba(0,0,0,0.6)" }}>
          El proceso no devolvió detalle.
        </Typography>
      )}
    </Box>
  );
}

export default function PrefacturasAdministracion() {
  const { setPageTitle } = useContext(GeneralContext);

  const periodos = useMemo(() => periodosDisponibles(), []);
  const [hasta, setHasta] = useState(periodos[1] ?? periodos[0]);
  const [desde, setDesde] = useState(periodos[3] ?? periodos[periodos.length - 1]);
  const [operadores, setOperadores] = useState([]);
  const [seleccionados, setSeleccionados] = useState([]);
  const [forzar, setForzar] = useState(false);

  const [pasos, setPasos] = useState([]);
  const [ejecutando, setEjecutando] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [error, setError] = useState(null);
  const [expandido, setExpandido] = useState(null);

  const detener = useRef(false);

  useEffect(() => {
    setPageTitle?.("GENERACIÓN DE PREFACTURAS");
  }, []);

  useEffect(() => {
    axios
      .getAllOperadores()
      .then((res) => setOperadores(Array.isArray(res?.data) ? res.data : []))
      .catch(() => {
        setOperadores([]);
        setError(
          "No se pudo cargar la lista de operadores: solo queda disponible la ejecución para todos."
        );
      });
  }, []);

  const rango = useMemo(() => periodosEnRango(desde, hasta), [desde, hasta]);
  const rangoInvalido = rango.length === 0;

  const objetivos = useMemo(
    () =>
      seleccionados.length
        ? seleccionados
        : [{ id_operador: null, nombre_operador: "Todos los operadores" }],
    [seleccionados]
  );

  const totalPasos = rango.length * PROCESOS.length * objetivos.length;
  const terminados = pasos.filter((p) => TERMINADOS.includes(p.estado)).length;
  const conError = pasos.filter((p) => p.estado === "error").length;
  const generadas = pasos
    .filter((p) => p.estado === "ok")
    .reduce((total, p) => total + (p.resultado?.exitosos?.length ?? 0), 0);

  const actualizarPaso = (indice, cambios) => {
    setPasos((previos) =>
      previos.map((paso, i) => (i === indice ? { ...paso, ...cambios } : paso))
    );
  };

  const ejecutar = async () => {
    const plan = construirPlan(rango, objetivos);
    setPasos(plan);
    setExpandido(null);
    setEjecutando(true);
    detener.current = false;

    const fallados = new Set();

    for (let i = 0; i < plan.length; i += 1) {
      const paso = plan[i];
      const clave = `${paso.periodo}-${paso.idOperador ?? "todos"}`;

      if (detener.current) {
        actualizarPaso(i, { estado: "cancelado" });
        continue;
      }

      if (paso.tipo === "operador" && fallados.has(clave)) {
        actualizarPaso(i, {
          estado: "omitido",
          mensaje: "No se ejecutó porque falló la prefactura SAGEC del mismo período.",
        });
        continue;
      }

      actualizarPaso(i, { estado: "en_curso" });

      try {
        const parametros = {
          periodo: paso.periodo,
          ...(paso.idOperador != null ? { id_operador: paso.idOperador } : {}),
        };
        const respuesta =
          paso.tipo === "sagec"
            ? await axios.exportPrefacturaSagec({ ...parametros, force_overwrite: forzar })
            : await axios.exportPrefacturaOperador(parametros);

        actualizarPaso(i, { estado: "ok", resultado: respuesta?.data ?? null });
      } catch (e) {
        fallados.add(clave);
        actualizarPaso(i, { estado: "error", mensaje: await describirError(e) });
      }
    }

    setEjecutando(false);
  };

  const confirmarEjecucion = () => {
    setConfirmando(false);
    ejecutar();
  };

  return (
    <PageBody>
      <Box width="100%">
        <Typography variant="h1" sx={{ textAlign: "center", p: "0.6rem" }}>
          GENERACIÓN DE PREFACTURAS
        </Typography>
        <Typography variant="body2" sx={{ textAlign: "center", mb: 3 }}>
          Ejecuta la prefactura SAGEC y luego la de operador, período por período, del más
          antiguo al más reciente.
        </Typography>

        <Box
          sx={{
            mb: 3,
            p: 2.5,
            border: "1px solid rgba(0,0,0,0.15)",
            borderRadius: 1,
            background: "#fff",
            display: "flex",
            alignItems: "flex-start",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box sx={{ flex: "0 0 200px" }}>
            <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
              Desde:
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={desde}
              disabled={ejecutando}
              onChange={(e) => setDesde(e.target.value)}
            >
              {periodos.map((p) => (
                <MenuItem key={p} value={p}>
                  {etiquetaPeriodo(p)}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ flex: "0 0 200px" }}>
            <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
              Hasta:
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={hasta}
              disabled={ejecutando}
              onChange={(e) => setHasta(e.target.value)}
            >
              {periodos.map((p) => (
                <MenuItem key={p} value={p}>
                  {etiquetaPeriodo(p)}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ flex: "1 1 340px", minWidth: 280 }}>
            <Typography variant="body1" sx={{ mb: 0.75, fontWeight: 500 }}>
              Operadores:
            </Typography>
            <Autocomplete
              multiple
              size="small"
              disabled={ejecutando}
              options={operadores}
              value={seleccionados}
              onChange={(evento, valor) => setSeleccionados(valor)}
              getOptionLabel={(o) => o.nombre_operador ?? `Operador ${o.id_operador}`}
              isOptionEqualToValue={(a, b) => a.id_operador === b.id_operador}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={seleccionados.length ? "" : "Todos los operadores"}
                />
              )}
            />
          </Box>

          <Box sx={{ alignSelf: "center", pt: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={forzar}
                  disabled={ejecutando}
                  onChange={(e) => setForzar(e.target.checked)}
                />
              }
              label={
                <Typography variant="body2">
                  Regenerar si ya existe (solo SAGEC)
                </Typography>
              }
            />
          </Box>

          <Box sx={{ alignSelf: "flex-end", display: "flex", gap: 1 }}>
            <Button
              variant="contained"
              startIcon={
                ejecutando ? <CircularProgress size={16} /> : <PlayArrowIcon />
              }
              disabled={ejecutando || rangoInvalido}
              onClick={() => setConfirmando(true)}
              sx={{
                background: "#C8D8F0",
                color: "#1565c0",
                boxShadow: "none",
                textTransform: "none",
                "&:hover": { background: "#b3c8e8", boxShadow: "none" },
              }}
            >
              {ejecutando ? "Generando..." : "Generar prefacturas"}
            </Button>
            <Button
              startIcon={<StopIcon />}
              disabled={!ejecutando}
              onClick={() => {
                detener.current = true;
              }}
              sx={{ textTransform: "none" }}
            >
              Detener
            </Button>
          </Box>

          <Box sx={{ flexBasis: "100%" }}>
            <Typography variant="caption" sx={{ color: "rgba(0,0,0,0.6)" }}>
              {rangoInvalido
                ? "El período inicial no puede ser posterior al final."
                : `${rango.length} ${rango.length === 1 ? "período" : "períodos"} × ${
                    PROCESOS.length
                  } procesos × ${objetivos.length} ${
                    objetivos.length === 1 ? "objetivo" : "objetivos"
                  } = ${totalPasos} llamadas secuenciales, de ${etiquetaPeriodo(
                    rango[0]
                  )} a ${etiquetaPeriodo(rango[rango.length - 1])}.`}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Paper
            elevation={0}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              p: 2,
              mb: 3,
              border: "1px solid rgba(198, 40, 40, 0.3)",
              background: "rgba(198, 40, 40, 0.08)",
            }}
          >
            <ErrorOutlineIcon sx={{ color: "#C62828" }} fontSize="small" />
            <Typography variant="body2" sx={{ color: "#C62828" }}>
              {error}
            </Typography>
          </Paper>
        )}

        {pasos.length > 0 && (
          <PageBgBlock pad="0">
            <Box sx={{ px: 2, pt: 2 }}>
              <LinearProgress
                variant="determinate"
                value={(terminados / pasos.length) * 100}
                sx={{ height: 6, borderRadius: 3 }}
              />
              <Typography variant="body2" sx={{ mt: 1, mb: 1.5 }}>
                {terminados} de {pasos.length} pasos · {numero(generadas)} prefacturas
                generadas
                {conError ? ` · ${conError} con error` : ""}
                {ejecutando ? "" : " · proceso detenido"}
              </Typography>
            </Box>

            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 40 }} />
                    <TableCell sx={{ fontWeight: 600 }}>Período</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Proceso</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Operador</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Estado</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Generadas
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="right">
                      Sin datos
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pasos.map((paso, indice) => {
                    const abierto = expandido === indice;
                    const exitosos = paso.resultado?.exitosos?.length ?? null;
                    const noEncontrados = paso.resultado?.no_encontrados?.length ?? null;
                    const tieneDetalle =
                      Boolean(paso.mensaje) || Boolean(paso.resultado);

                    return (
                      <React.Fragment key={`${paso.periodo}-${paso.tipo}-${paso.idOperador ?? "todos"}`}>
                        <TableRow
                          hover
                          sx={{ cursor: tieneDetalle ? "pointer" : "default" }}
                          onClick={() =>
                            tieneDetalle && setExpandido(abierto ? null : indice)
                          }
                        >
                          <TableCell>
                            {tieneDetalle &&
                              (abierto ? (
                                <KeyboardArrowDownIcon fontSize="small" />
                              ) : (
                                <KeyboardArrowRightIcon fontSize="small" />
                              ))}
                          </TableCell>
                          <TableCell>{etiquetaPeriodo(paso.periodo)}</TableCell>
                          <TableCell>{paso.etiquetaProceso}</TableCell>
                          <TableCell>{paso.nombreOperador}</TableCell>
                          <TableCell>
                            <EstadoChip estado={paso.estado} />
                          </TableCell>
                          <TableCell align="right">
                            {exitosos == null ? "—" : numero(exitosos)}
                          </TableCell>
                          <TableCell align="right">
                            {noEncontrados == null ? "—" : numero(noEncontrados)}
                          </TableCell>
                        </TableRow>
                        {abierto && (
                          <TableRow>
                            <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                              <DetallePaso paso={paso} />
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </PageBgBlock>
        )}

        <ModalStandardCancel
          open={confirmando}
          error
          onClose={() => setConfirmando(false)}
          onClick={confirmarEjecucion}
          btnText="Generar"
          title="Generar prefacturas"
          qst={`${totalPasos} ${totalPasos === 1 ? "llamada" : "llamadas"} sobre ${
            rango.length
          } ${rango.length === 1 ? "período" : "períodos"}`}
          msg={`Se generarán archivos y se registrarán versiones para ${
            seleccionados.length
              ? seleccionados.map((o) => o.nombre_operador).join(", ")
              : "todos los operadores"
          }, de ${etiquetaPeriodo(rango[0])} a ${etiquetaPeriodo(
            rango[rango.length - 1]
          )}${forzar ? ", regenerando las prefacturas SAGEC ya existentes" : ""}.`}
        />
      </Box>
    </PageBody>
  );
}
